import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  BrandingService,
  CompanyBranding,
  CompanyBrandingHelper,
  HorizontalListData,
  LiquidityEvent,
  LoanHelper,
  MessageService,
} from 'common';
import { AccountSummaryAdapter } from './account-summary.data';
import { LiquidityEventFacade } from '../../liquidity-event/domain/+state/liquidity-event.facade';
import { LoanFacade } from '../../loan/domain/+state/loan.facade';
import { LoanData } from '../../loan/domain/models/loan.model';
import { LitigationPayoffNotesDialogComponent } from '../../loan/litigation-payoff-notes-dialog/litigation-payoff-notes-dialog.component';
import { LitigationPayoffNotesDialogResult } from '../../loan/litigation-payoff-notes-dialog/litigation-payoff-notes-dialog.model';
import { LoanDescriptionDialogComponent } from '../../loan/loan-description-dialog/loan-description-dialog.component';
import { AccountStatusIconChipModel } from '../account-status-icon-chip/account-status-icon-chip.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'ifp-account-summary',
    templateUrl: './account-summary.component.html',
    styleUrls: ['./account-summary.component.scss'],
    providers: [AccountSummaryAdapter],
    standalone: false
})
export class AccountSummaryComponent implements OnInit, OnChanges {
  @Input() loan: LoanData;
  @Input() liquidityEvent: LiquidityEvent;

  private saveSub: Subscription;
  accountStatusIconChipModel: AccountStatusIconChipModel;
  listItems: HorizontalListData[];

  constructor(
    private readonly loanFacade: LoanFacade,
    private readonly liquidityEventFacade: LiquidityEventFacade,
    private readonly dialog: MatDialog,
    private readonly messageService: MessageService,
    private readonly brandingService: BrandingService,
    private readonly adapter: AccountSummaryAdapter,
  ) {}

  ngOnInit(): void {
    this.brandingService
      .getBranding$()
      .pipe(untilDestroyed(this))
      .subscribe((brandingData: CompanyBranding) => {
        this.initAccountStatusIconChipModel(brandingData);
      });
  }

  ngOnChanges(): void {
    this.listItems = this.adapter.adaptListData(this.loan);
  }

  editDescription(): void {
    LoanDescriptionDialogComponent.show(this.dialog, {
      ...this.loan,
    }).subscribe((result) => {
      if (result !== undefined) {
        this.saveSub = this.loanFacade.updateDescription(result, this.loan.id).subscribe({
          next: this.saveSuccessHandler.bind(this),
          error: this.saveErrorHandler.bind(this),
        });
      }
    });
  }

  editPayoffNote(liquidityEvent: LiquidityEvent): void {
    LitigationPayoffNotesDialogComponent.show(this.dialog, { ...this.loan }, { ...liquidityEvent }).subscribe(
      (dialogResult: LitigationPayoffNotesDialogResult) => {
        if (dialogResult !== undefined) {
          this.saveSub = this.loanFacade
            .updatePayoffNotes(dialogResult.note, dialogResult.litigationId, dialogResult.loanId)
            .pipe(switchMap(() => this.liquidityEventFacade.refreshLitigation(dialogResult.loanId)))
            .subscribe({
              next: this.saveSuccessHandler.bind(this),
              error: this.saveErrorHandler.bind(this),
            });
        }
      },
    );
  }

  private saveSubClear(): void {
    if (this.saveSub) this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private saveSuccessHandler(): void {
    this.saveSubClear();
  }

  private initAccountStatusIconChipModel(brandingData: CompanyBranding): void {
    const advisorPhone = CompanyBrandingHelper.getAdvisorPhone(brandingData, this.loan?.advisorPhone);

    this.accountStatusIconChipModel = {
      needsAttentionMessage: LoanHelper.getNeedsAttentionMessage({ ...this.loan, advisorPhone: advisorPhone }),
    };
  }
}
